/**
 * Which zone the panel renders instants in — the VISITOR's, resolved from their
 * own runtime, and never a throw.
 *
 * WHY THIS FILE EXISTS RATHER THAN A CALL TO `browserTimeZone()`.
 *
 * `@axon/i18n` already reads the runtime's zone, and it is right for the console:
 *
 *     export function browserTimeZone(): TimeZone {
 *       return resolveTimeZone(new Intl.DateTimeFormat().resolvedOptions().timeZone);
 *     }
 *
 * `resolveTimeZone` THROWS on anything it cannot vouch for, which is the correct
 * contract for a package whose whole purpose is that no formatter is ever handed
 * an unchecked zone. It is the wrong contract HERE, and the difference is where
 * the code runs. In the console a broken zone is an internal tool showing an
 * error to an employee. In the widget it is a `RangeError` raised while the
 * module graph is still evaluating — `src/vendor/i18n.tsx` called
 * `browserTimeZone()` at MODULE SCOPE — so the whole app fails to load and a
 * member of the public looking at a customer's site gets a panel that never
 * paints, on a page whose owner did not choose our stack. Wrong times are a bug;
 * no widget at all is an outage at somebody else's shop.
 *
 * Three real inputs take that path, and none of them is exotic:
 *   - `""` and `"Etc/Unknown"` — both throw `RangeError` out of
 *     `Intl.DateTimeFormat`, and `Etc/Unknown` is what ICU reports when the host
 *     zone cannot be mapped;
 *   - `"+03:00"` — accepted by ES2024 `Intl`, refused by `resolveTimeZone` on
 *     purpose (see the DST argument in `packages/i18n/src/time-zone.ts`), and
 *     exactly what several anti-fingerprinting extensions substitute;
 *   - a missing or patched `Intl`, where `new Intl.DateTimeFormat()` itself
 *     throws a `TypeError` that `isTimeZone` deliberately rethrows.
 *
 * So this module resolves the same value and DEGRADES: every failure becomes
 * `{ timeZone: UTC, source: 'fallback' }`, and the `source` is the point — it
 * travels to `format.ts`, which qualifies every time it renders in a zone we did
 * not detect. A labelled wrong time is recoverable by the reader. An unlabelled
 * one is the missed appointment.
 *
 * THE ZONE IS NOT A LOCATION, AND THAT SENTENCE IS THIS FIELD'S ENTIRE LICENCE.
 *
 * What is collected: one IANA identifier the visitor's own runtime volunteers,
 * naming a set of DST rules — `Europe/Kyiv` is the rule set roughly forty million
 * people share, `Europe/Berlin` covers five countries. It is NOT derived from an
 * IP address, it is not a coordinate, it does not narrow to a city, and nothing
 * here asks the Geolocation API, which would prompt and would be a different
 * category of data entirely.
 *
 * Why it is NECESSARY, which is the test every field in this widget has to pass:
 * without it every timestamp in the transcript is rendered in some other party's
 * day. The visitor is reading when a reply arrived and deciding whether to wait.
 * There is no way to render an instant to a human without a zone; the only
 * choice is whose, and using the tenant's would mean showing a Ukrainian
 * business's clock to a visitor in Lisbon.
 *
 * The limits that keep it necessary rather than merely useful:
 *   - it stays in this frame. Nothing here sends it anywhere, and
 *     `WidgetSessionRequestSchema` has no field for it. If it is ever sent, it
 *     becomes stored personal data on `person.timezone` and must be declared
 *     `@pii` in the prisma schema so the erasure inventory reaches it — that is
 *     a contract change with a reviewer, not an import;
 *   - it is ONE field, taken alone. A zone plus a language plus a screen size is
 *     the start of a device fingerprint, and this module stops at the zone
 *     because combining is the step that turns three innocuous facts into an
 *     identifier that follows a person across sites;
 *   - it is read, not stored: no cookie, no `localStorage`, nothing that
 *     outlives the frame.
 *
 * WHY UTC IS THE STATED DEFAULT. It is a statement of ignorance rather than a
 * guess about a person: falling back to the brand's zone would invent a location
 * for the one visitor who withheld theirs, and hardened browsers already report
 * UTC on purpose. Its cost is named rather than hidden — a visitor behind a
 * spoofing extension reads `GMT+0` times instead of their own, and `format.ts`
 * makes sure they can SEE that, which is the only reason the cost is acceptable.
 *
 * WHAT BREAKS IF THIS CHANGES. Return a bare `TimeZone` instead of the pair and
 * `format.ts` can no longer tell a detected zone from a default, so it either
 * labels every time (noise) or none (the missed appointment). Throw instead of
 * degrading and the widget stops loading for the runtimes listed above.
 */
import { isTimeZone, resolveTimeZone, UTC_TIME_ZONE, type TimeZone } from '@axon/i18n';

/**
 * Where the zone came from. `device` means the runtime named a zone we could
 * check; `fallback` means we do not know the visitor's zone and are saying so.
 */
export type TimeZoneSource = 'device' | 'fallback';

export interface VisitorTimeZone {
  readonly timeZone: TimeZone;
  readonly source: TimeZoneSource;
}

/**
 * UTC, branded.
 *
 * The `catch` is not decoration. This constant is the value used when the
 * runtime's `Intl` is broken, so building it must not itself depend on that
 * `Intl` working — a module-scope throw here would reintroduce the exact failure
 * this file exists to remove. `UTC_TIME_ZONE` is valid by definition, so the
 * assertion asserts something true rather than papering over a check.
 */
const FALLBACK_ZONE: TimeZone = brandUtc();

/**
 * The one answer given whenever the visitor's zone is not known. Shared rather
 * than rebuilt so that "we did not detect a zone" is one value in this module
 * instead of several that have to agree.
 */
const NOT_DETECTED: VisitorTimeZone = Object.freeze({
  timeZone: FALLBACK_ZONE,
  source: 'fallback',
});

function brandUtc(): TimeZone {
  try {
    return resolveTimeZone(UTC_TIME_ZONE);
  } catch {
    return UTC_TIME_ZONE as TimeZone;
  }
}

/**
 * The zone identifier the runtime reports, or `null` if it reports nothing
 * usable. Impure and isolated here so that everything below it is a pure function
 * a test can drive with the values real devices actually send.
 */
export function readDeviceTimeZone(): string | null {
  try {
    // Annotated rather than inferred: lib.dom types `timeZone` as a `string` that
    // is always there, and the runtimes this file is about are the ones that
    // disagree. Writing the optionality down is what makes the guard below a
    // real check instead of one the compiler prunes as unreachable.
    const resolved: { readonly timeZone?: string | undefined } = new Intl.DateTimeFormat(
      undefined,
    ).resolvedOptions();
    const zone = resolved.timeZone;
    if (typeof zone !== 'string' || zone.trim() === '') {
      return null;
    }
    return CANONICAL_ZONE[zone] ?? zone;
  } catch {
    // `Intl` absent, or replaced by an extension that throws. Not our problem to
    // diagnose; our problem is to keep rendering.
    return null;
  }
}

/**
 * IANA renamed a handful of zones and kept the old names as aliases; runtimes
 * still REPORT the old ones. Chrome answers `Europe/Kiev` for a device in Kyiv to
 * this day, and that string travelled unchanged into `person.timezone` and onto
 * the agent's contact card as "Their time zone: Europe/Kiev" — accurate, and the
 * one spelling this product's own users read as a mistake. The alias is mapped
 * to the canonical name at the source, so the formatter and the contact card see
 * the same `Europe/Kyiv`; both spellings are valid to `Intl`, so nothing
 * downstream has to know this table exists.
 */
const CANONICAL_ZONE: Readonly<Record<string, string>> = Object.freeze({
  'Europe/Kiev': 'Europe/Kyiv',
});

/**
 * Check one candidate zone and say where the answer came from. Never throws.
 *
 * An OFFSET (`+03:00`) is refused rather than honoured, and that is a real cost
 * paid on purpose: honouring it would be right today and wrong after the next DST
 * transition, and `resolveTimeZone` will not brand one — which is the package
 * making the decision, not this file. The visitor sees `GMT+0` and can tell.
 */
export function resolveVisitorTimeZone(candidate: string | null | undefined): VisitorTimeZone {
  if (typeof candidate === 'string' && candidate.trim() !== '') {
    const zone = candidate.trim();
    try {
      if (isTimeZone(zone)) {
        return { timeZone: resolveTimeZone(zone), source: 'device' };
      }
    } catch {
      // `isTimeZone` returns false for an unknown zone but RETHROWS anything
      // else, on the argument that a non-RangeError means a broken runtime
      // rather than a bad input. It is right, and a broken runtime is still not
      // a reason to stop drawing the panel — so the failure is not swallowed, it
      // is ANSWERED, with the same value and the same `fallback` marker that
      // every other unusable input gets. The trace it leaves is the one the
      // visitor can see: `format.ts` prints the offset of a zone marked this way.
      return NOT_DETECTED;
    }
  }
  return NOT_DETECTED;
}

/**
 * The visitor's zone, read and checked. The one call an app makes; everything
 * else here is exported for the tests and for a caller that already holds a
 * candidate.
 */
export function visitorTimeZone(): VisitorTimeZone {
  return resolveVisitorTimeZone(readDeviceTimeZone());
}
